import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, ArrowLeft, Inbox } from 'lucide-react';
import { Link } from 'react-router-dom';

export const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, y = 26, className = '' }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function MaskedLine({ children, delay = 0, className = '' }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: '112%' }}
        animate={{ y: 0 }}
        transition={{ duration: 0.95, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function StatusChip({ done, doneLabel = 'Sudah Dicek', todoLabel = 'Perlu Dicek', testid }) {
  return done ? (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full bg-[#EBF2EC] border border-[#CBDCCE] text-sage text-[11px] font-semibold px-2.5 py-1">
      <CheckCircle2 size={12} strokeWidth={2.2} /> {doneLabel}
    </span>
  ) : (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full bg-[#FBF1DF] border border-[#EEDCB9] text-amberw text-[11px] font-semibold px-2.5 py-1">
      <AlertCircle size={12} strokeWidth={2.2} /> {todoLabel}
    </span>
  );
}

export function Money({ children, className = '' }) {
  return <span className={`font-mono tnum tracking-tight ${className}`}>{children}</span>;
}

export function SectionHead({ eyebrow, title, desc, light = false }) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <p className={`font-sans font-semibold text-[13px] sm:text-sm uppercase tracking-[0.24em] mb-3 ${light ? 'text-gold' : 'text-terra'}`}>{eyebrow}</p>
      )}
      <h2 className={`font-serif text-2xl sm:text-3xl font-semibold tracking-tight leading-snug ${light ? 'text-ivory' : 'text-ink'}`}>{title}</h2>
      {desc && <p className={`mt-2 text-sm leading-relaxed ${light ? 'text-ivory/70' : 'text-slate-600'}`}>{desc}</p>}
    </div>
  );
}

export function BackBar({ to, title }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <Link data-testid="back-link" to={to} className="w-9 h-9 rounded-full border border-ink/15 bg-white flex items-center justify-center text-ink hover:bg-bone transition-colors">
        <ArrowLeft size={16} />
      </Link>
      {title && <span className="text-sm font-semibold text-ink">{title}</span>}
    </div>
  );
}

export function EmptyState({ title, desc, children }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-dashed border-ink/20 bg-white/60 p-8 text-center">
      <div className="kawung absolute inset-0 opacity-[0.03] pointer-events-none" />
      <Inbox className="mx-auto text-terra mb-3" size={28} strokeWidth={1.5} />
      <p className="font-serif text-lg font-semibold text-ink">{title}</p>
      {desc && <p className="mt-1 text-sm text-slate-500 max-w-xs mx-auto">{desc}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

export function Field({ label, optional, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-ink mb-1.5">
        {label} {optional && <span className="text-slate-400 font-normal">· opsional</span>}
      </span>
      {children}
    </label>
  );
}

export const inputCls =
  'w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-pewter placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-terra/40 focus:border-terra transition';
